/** Settings → Planning → Notifications, the delivery log and Email preferences (planning P4-1a, design §5.7, §5.8). */
export default {
  notifications: {
    title: 'Notifications',
    description:
      'Email applicants, officers and technical departments about what happens on their cases. Emails carry the reference, dates, step names, the council name and a link; nothing else.',
    enabled: 'Send email notifications',
    enabledHint: 'Off by default. Only what happens after you turn this on is emailed.',
    enableNeedsTest: 'Send a test email to yourself first, using the button below.',
    enabledSince: 'On since {{date}}.',
    modesTitle: 'How each email is sent',
    kinds: {
      applicant_update: 'Updates to the applicant',
      applicant_document_shared: 'A document shared with the applicant',
      applicant_reminder: 'Reminders to the applicant',
      officer_work: 'Work for officers',
      officer_referral_answered: 'Technical comments received',
      officer_reminder: 'Reminders to officers',
      department_referral: 'Requests for technical comments',
      department_reminder: 'Reminders to departments',
      test: 'Test email',
      digest: 'Daily digest',
    },
    modes: { off: 'Off', immediate: 'Straight away', digest: 'In the daily digest' },
    offsetsTitle: 'Reminders',
    offsetsHint: 'Days before the date, separated by commas.',
    offsetsApplicant: 'Days before a response date',
    offsetsLapse: 'Days before the permission lapses',
    appealDates: 'Include appeal dates',
    appealDatesHint: 'When off, emails and reminders do not mention appeal windows.',
    digestHour: 'Hour of the daily digest (0 to 23)',
    defaultLocale: 'Language when a person has not chosen one',
    locales: { en: 'English', ms: 'Malay', zh: 'Chinese' },
    replyTo: 'Reply-To address',
    replyToHint: 'Replies go to this mailbox, for example the council’s OSC mailbox.',
    problems: {
      offsets: 'Whole days from {{min}} to {{max}}, at most three, none twice.',
      digestHour: 'Enter a whole hour from 0 to 23.',
      replyTo: 'Enter one email address.',
    },
    save: 'Save notifications',
    saving: 'Saving…',
    saved: 'Notification settings saved.',
    log: {
      title: 'Delivery log',
      senderLabel: 'Mail service',
      senderSes: 'Sent through Amazon SES',
      senderSmtp: 'Sent through your mail relay',
      relayNoFeedback: 'Your mail relay reports no bounces: Sent means the relay accepted the message.',
      noRelay: 'No mail relay configured',
      countsLabel: 'Messages by status',
      awaitingDigest: 'Awaiting digest',
      awaitingDigestHint: 'Digest messages wait here until the next daily digest goes out.',
      held: 'Held for a letter',
      heldListLabel: 'Decisions waiting for a letter',
      heldItem: '{{caseRef}}: decision at step {{step}}',
      overCap: 'Over the recipient limit',
      failuresLabel: 'Failures by reason',
      failure: '{{reason}}: {{count}}',
      filterLabel: 'Case reference',
      filter: 'Filter',
      clearFilter: 'Clear filter',
      columns: {
        kind: 'Email',
        caseRef: 'Case',
        recipient: 'Recipient',
        status: 'Status',
        attempts: 'Attempts',
        created: 'Created',
        action: 'Action',
      },
      empty: 'No message has been logged yet.',
      more: 'Show more',
      resend: 'Resend',
      resendLabel: 'Resend the email to {{address}}',
    },
    statuses: {
      queued: 'Queued',
      sent: 'Sent',
      failed: 'Failed',
      suppressed: 'Not sent',
      bounced: 'Bounced',
      complained: 'Marked as spam',
    },
    codes: {
      not_visible: 'The person can no longer see the case',
      opted_out: 'The person turned this email off',
      no_address: 'The person has no email address',
      address_suppressed: 'The address bounced or complained before',
      not_configured: 'No mail relay configured',
      no_link_base: 'No console address to link to',
      over_cap: 'Too many recipients',
      stale: 'Too old to send',
      throttled: 'The mail service asked to slow down',
      rejected: 'Refused by the mail service',
      invalid_address: 'The address is not valid',
      auth_failed: 'The mail service refused the credentials',
      timeout: 'The mail service did not answer in time',
      unavailable: 'The mail service could not be reached',
      unknown: 'Unknown mail service failure',
    },
    test: {
      send: 'Send a test email to me',
      sending: 'Sending…',
      sent: 'Test email sent to {{address}}.',
      failed: 'The test email was not sent: {{reason}}.',
    },
    unavailable: {
      not_configured: 'No mail relay is configured, so a test email cannot be sent.',
      no_address: 'Your account has no email address to send a test to.',
      not_tenant_identity:
        'Your account does not belong to this workspace. Sign in with one of its accounts to send a test.',
      settings_not_saved: 'Save the Planning settings first, then send a test email.',
      no_link_base: 'This installation has no console address to link to, so no email could reach anyone.',
    },
    preferences: {
      open: 'Email preferences',
      title: 'Email preferences',
      intro: 'Choose which planning emails you receive. Turning an email off never hides anything in the console.',
      updates: 'Updates about my cases',
      reminders: 'Reminders of dates',
      digests: 'Daily digest',
      lastChanged: 'Last changed {{date}}.',
      save: 'Save',
      saving: 'Saving…',
      cancel: 'Cancel',
    },
  },
  errors: {
    notifications_test_required: 'Send a test email to yourself before turning notifications on.',
    notification_test_unavailable:
      'A test email cannot be sent: no mail relay is set up, or your account has no address.',
    notification_test_limit: 'At most five test emails an hour. Try again later.',
    notification_not_found: 'That message was not found.',
    notification_not_resendable: 'Only a failed or stale message can be sent again.',
    notifications_off: 'Notifications are off. Turn them on to send a message again.',
  },
} as const
